/**
 * MasterDataVesselNameFormDialog — Create/Edit `d_vessel_name`.
 * Compact Sales Contract style dialog. Single-column.
 *
 * PK `id` là AUTO-INT (DB-generated): create KHÔNG có field id;
 * edit hiện `id` readonly. Update dùng { id, body }.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdVesselNameMutation,
  useUpdateMdVesselNameMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataVesselName } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataVesselName | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  vessel_name: string
}

const EMPTY: FormState = {
  vessel_name: '',
}

export default function MasterDataVesselNameFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createVesselName, { isLoading: creating }] = useCreateMdVesselNameMutation()
  const [updateVesselName, { isLoading: updating }] = useUpdateMdVesselNameMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      setForm({
        vessel_name: initialValue.vessel_name ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const canSave = !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    vessel_name: form.vessel_name.trim() || null,
  })

  const handleSave = async () => {
    try {
      if (isEdit && initialValue) {
        await updateVesselName({ id: initialValue.id, body: buildBody() }).unwrap()
        snack.success('Vessel name updated')
      } else {
        await createVesselName(buildBody()).unwrap()
        snack.success('Vessel name created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Vessel Name' : 'New Vessel Name'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {isEdit && initialValue ? (
            <Grid item xs={12}>
              <TextField
                label="ID"
                size="small"
                fullWidth
                value={initialValue.id}
                InputLabelProps={{ shrink: true }}
                inputProps={{ readOnly: true, tabIndex: -1 }}
                helperText="ID is auto-generated."
                sx={roSx}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label="Vessel Name"
              size="small"
              fullWidth
              value={form.vessel_name}
              onChange={(e) => set('vessel_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
